import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Menu,
  X,
  Phone,
  ChevronDown,
  ArrowUpRight,
  Wrench,
  Droplets,
  Activity,
  Gauge,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BUSINESS, SERVICES, NAV } from "@/lib/site";

const ICONS = { Wrench, Droplets, Activity, Gauge };

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isServiceActive = SERVICES.some((s) => s.path === location.pathname);
  const transparent = location.pathname === "/" && !scrolled;
  // Desktop (lg+) header is dark on the homepage hero AND when scrolled on any page.
  // Nav text is hidden below lg, so this only affects desktop text colouring.
  const darkHeader = transparent || scrolled;

  return (
    <>
    <header
      data-testid="site-header"
      className={cn(
        "sticky top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300",
        transparent
          ? "border-transparent bg-transparent"
          : scrolled
            ? "border-border bg-white/90 backdrop-blur-xl shadow-[0_1px_20px_rgba(24,24,27,0.05)] lg:border-white/10 lg:bg-[rgba(12,18,28,0.94)] lg:shadow-none lg:backdrop-blur-md"
            : "border-transparent bg-white"
      )}
    >
      <div className="container-x flex h-16 items-center justify-between md:h-[72px]">
        <Link
          to="/"
          data-testid="site-logo"
          aria-label="NorthWork Cyprus — home"
          className="inline-flex items-center"
        >
          <img
            src="/images/logo-full.webp"
            alt="NorthWork Cyprus — Air Conditioning Services"
            className={cn(
              "h-9 w-auto sm:h-10",
              !transparent && "[filter:drop-shadow(0_0_1px_rgba(24,24,27,0.55))]"
            )}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          <NavLink to="/" active={location.pathname === "/"} light={darkHeader}>
            Home
          </NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-services-trigger"
              className={cn(
                "flex items-center gap-1 text-sm font-medium tracking-wide transition-colors",
                isServiceActive
                  ? "text-brand"
                  : darkHeader
                    ? "text-white/90 hover:text-white"
                    : "text-charcoal hover:text-brand"
              )}
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
            >
              Services
              <ChevronDown
                className={cn("h-4 w-4 transition-transform duration-300", servicesOpen && "rotate-180")}
              />
            </button>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full z-50 w-[640px] -translate-x-1/2 pt-4"
                >
                  <div className="grid grid-cols-2 gap-1 border border-border bg-white p-2 shadow-[0_24px_60px_-24px_rgba(24,24,27,0.28)]">
                    {SERVICES.map((s) => {
                      const Icon = ICONS[s.icon];
                      return (
                        <Link
                          key={s.key}
                          to={s.path}
                          data-testid={`nav-dropdown-${s.key}`}
                          className="group flex gap-3.5 p-4 transition-colors hover:bg-surface"
                        >
                          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center border border-border bg-white text-brand transition-colors group-hover:border-brand">
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-1 font-heading text-[15px] font-semibold text-charcoal">
                              {s.title}
                              <ArrowUpRight className="h-3.5 w-3.5 -translate-y-px text-charcoal-muted opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100" />
                            </span>
                            <span className="mt-1 line-clamp-2 block text-[13px] leading-snug text-charcoal-muted">
                              {s.short}
                            </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink to="/about" active={location.pathname === "/about"} light={darkHeader}>
            About
          </NavLink>
          <NavLink to="/contact" active={location.pathname === "/contact"} light={darkHeader}>
            Contact
          </NavLink>
        </nav>

        {/* Call button */}
        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid="header-call-button"
            className="group hidden items-center gap-2.5 rounded-sm bg-brand px-5 py-3 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-brand-hover md:inline-flex"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden xl:inline">Call </span>
            {BUSINESS.phoneDisplay}
          </a>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            className={cn(
              "grid h-11 w-11 place-items-center border transition-colors lg:hidden",
              transparent
                ? "border-white/30 text-white hover:bg-white/10"
                : "border-border text-charcoal hover:bg-surface"
            )}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      </header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-charcoal text-white lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="container-x flex h-16 items-center justify-between border-b border-white/10 md:h-[72px]">
              <Link to="/" aria-label="NorthWork Cyprus — home" className="inline-flex items-center">
                <img
                  src="/images/logo-full.webp"
                  alt="NorthWork Cyprus — Air Conditioning Services"
                  className="h-9 w-auto"
                />
              </Link>
              <button
                type="button"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="grid h-11 w-11 place-items-center border border-white/20 text-white transition-colors hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="container-x flex flex-1 flex-col overflow-y-auto py-8">
              <nav className="flex flex-col">
                <MobileLink to="/" label="Home" onClick={() => setMobileOpen(false)} />
                <div className="border-b border-white/10 py-5">
                  <span className="font-mono-t text-[11px] uppercase tracking-[0.22em] text-white/40">
                    Services
                  </span>
                  <div className="mt-4 flex flex-col gap-3">
                    {SERVICES.map((s) => {
                      const Icon = ICONS[s.icon];
                      return (
                        <Link
                          key={s.key}
                          to={s.path}
                          data-testid={`mobile-service-${s.key}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 text-white/90"
                        >
                          <Icon className="h-5 w-5 text-brand" />
                          <span className="font-heading text-lg">{s.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <MobileLink to="/about" label="About" onClick={() => setMobileOpen(false)} />
                <MobileLink to="/contact" label="Contact" onClick={() => setMobileOpen(false)} />
              </nav>

              <div className="mt-auto flex flex-col gap-3 pt-8">
                <a
                  href={`tel:${BUSINESS.phoneTel}`}
                  data-testid="mobile-call-button"
                  className="flex items-center justify-center gap-2.5 bg-brand px-6 py-4 text-sm font-medium text-white"
                >
                  <Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}
                </a>
                <a
                  href={BUSINESS.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-button"
                  className="flex items-center justify-center gap-2.5 border border-white/25 px-6 py-4 text-sm font-medium text-white transition-colors hover:bg-white/10"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function NavLink({ to, active, light, children }) {
  return (
    <Link
      to={to}
      className={cn(
        "relative text-sm font-medium tracking-wide transition-colors",
        active ? "text-brand" : light ? "text-white/90 hover:text-white" : "text-charcoal hover:text-brand"
      )}
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="border-b border-white/10 py-5 font-heading text-2xl font-medium text-white"
    >
      {label}
    </Link>
  );
}
